import { Laptop, Database, Battery, Monitor, Cpu, ShieldCheck, Zap, AlertTriangle, Speaker, Bug, Activity } from 'lucide-react';

export interface ServiceTypeConfig {
  slugSuffix: string; // e.g., 'data-recovery'
  titleTemplate: string; // e.g., 'Data Recovery in {suburb}'
  navLabel: string; // e.g., 'Data Recovery'
  icon: any;
  heroText: string;
  description: string;
  pricingText: string;
  faqs: { question: string; answer: string }[];
}

export const SERVICE_TYPES: ServiceTypeConfig[] = [
  {
    slugSuffix: 'laptop-repairs',
    titleTemplate: 'Laptop Repairs {suburb}',
    navLabel: 'Laptop Repairs',
    icon: Laptop,
    heroText: "Fast, reliable laptop repairs for {suburb} residents. We fix screens, keyboards, and power issues same-day.",
    description: "If you live in {suburb} and your laptop is running slow, overheating, or simply won't turn on, Ike is your local expert. We provide comprehensive diagnostics and board-level repairs that most shops can't handle. Don't drive to the city—we offer mobile service to {suburb} or priority drop-off nearby.",
    pricingText: "$170 Fixed Labor Fee (+ Parts)",
    faqs: [
      { question: "Do you come to {suburb}?", answer: "Yes! We offer mobile laptop repairs in {suburb} with no travel fees for local residents." },
      { question: "How long does a general repair take?", answer: "Most software and standard hardware issues are resolved within 24 hours." }
    ]
  },
  {
    slugSuffix: 'macbook-repairs',
    titleTemplate: 'MacBook Repairs {suburb}',
    navLabel: 'MacBook Repairs',
    icon: Cpu,
    heroText: "Expert Apple MacBook logic board and screen repairs in {suburb}. Save hundreds compared to the Apple Store.",
    description: "MacBooks are complex machines. Whether it's a MacBook Air with a dead battery or a MacBook Pro with liquid damage, {suburb} locals trust Ike. We use genuine-quality parts and perform component-level microsoldering to save devices that others condemn.",
    pricingText: "$170 - $250 Labor (Model Dependent)",
    faqs: [
      { question: "Can you fix water damaged MacBooks in {suburb}?", answer: "Yes. We specialize in liquid damage recovery. Please turn it off and contact us immediately." },
      { question: "Is it cheaper than the Apple Store?", answer: "Significantly. We repair the logic board instead of replacing the whole unit, often saving you over $600." }
    ]
  },
  {
    slugSuffix: 'laptop-battery-replacement',
    titleTemplate: 'Laptop Battery Replacement {suburb}',
    navLabel: 'Battery Replacement',
    icon: Battery,
    heroText: "Is your battery dying fast? We supply and fit high-quality batteries for laptops in {suburb}.",
    description: "A swollen or failing battery is a fire hazard. If your trackpad is lifting or your laptop only lasts 30 minutes, you need a replacement. We stock high-quality batteries for HP, Dell, Lenovo, and Apple devices, offering {suburb} residents a quick turnaround.",
    pricingText: "$180 - $280 (Supply & Fit)",
    faqs: [
      { question: "Do you have batteries in stock?", answer: "We stock common models. Rare models can be ordered and fitted within 2-3 days." },
      { question: "Is the battery warranty included?", answer: "Yes, all our battery replacements come with a 12-month Australian warranty." }
    ]
  },
  {
    slugSuffix: 'data-recovery',
    titleTemplate: 'Data Recovery {suburb}',
    navLabel: 'Data Recovery',
    icon: Database,
    heroText: "Lost your files? Professional forensic data recovery services available for {suburb} locals.",
    description: "Hard drive failure can be devastating. Whether it's family photos or business documents, our lab near {suburb} is equipped with advanced forensic tools to recover data from clicking drives, dead SSDs, and corrupted USB sticks. We have a 'No Data, No Fee' policy for standard recoveries.",
    pricingText: "$170 - $600 (Depending on Severity)",
    faqs: [
      { question: "My hard drive is clicking, what should I do?", answer: "Turn it off immediately! Continued spinning can destroy the data permanently. Call us for an assessment." },
      { question: "Can you recover data from water damaged laptops?", answer: "Yes, we often recover data from corroded motherboards by repairing the power rails to the storage chips." }
    ]
  },
  {
    slugSuffix: 'laptop-screen-replacement',
    titleTemplate: 'Laptop Screen Replacement {suburb}',
    navLabel: 'Screen Replacement',
    icon: Monitor,
    heroText: "Cracked screen? We replace LED and OLED laptop screens in {suburb} same-day.",
    description: "Don't squint through cracks. We provide professional screen replacement services for {suburb}. We handle standard 15.6\" panels, high-refresh rate gaming screens, and delicate MacBook Retina displays. Our installations ensure perfect bezel alignment and zero dead pixels.",
    pricingText: "From $240 (Fitted)",
    faqs: [
      { question: "How long does a screen replacement take?", answer: "If we have the panel in stock, it takes about 1 hour. Special orders take 1-2 days." },
      { question: "Do you fix touchscreens?", answer: "Yes, we replace touchscreen assemblies for HP Envy, Dell XPS, and Microsoft Surface devices." }
    ]
  },
  {
    slugSuffix: 'pc-repairs',
    titleTemplate: 'PC Repairs & Custom Builds {suburb}',
    navLabel: 'PC Repairs',
    icon: ShieldCheck,
    heroText: "Desktop PC troubleshooting, upgrades, and custom gaming builds in {suburb}.",
    description: "From silent office PCs to high-performance gaming rigs, we know desktops inside out. {suburb} residents rely on us for PSU replacements, GPU upgrades, and fixing 'No Boot' errors. We also offer cable management and thermal optimization services.",
    pricingText: "$170 Fixed Labor",
    faqs: [
      { question: "My PC won't turn on, is it the power supply?", answer: "It's a common cause. We can test your PSU and motherboard to identify the exact fault." },
      { question: "Do you build custom gaming PCs?", answer: "Yes! You buy the parts (or we source them), and we build it professionally with perfect cable management." }
    ]
  },
  {
    slugSuffix: 'blue-screen-repair',
    titleTemplate: 'Blue Screen & Boot Fix {suburb}',
    navLabel: 'Boot Problems',
    icon: AlertTriangle,
    heroText: "Stuck in a boot loop or seeing Blue Screens in {suburb}? We diagnose and fix OS crashes.",
    description: "A Blue Screen of Death (BSOD) or a computer that keeps restarting is often a sign of corrupt drivers or failing hardware. {suburb} locals trust us to interpret the STOP code errors and perform the precise fix—whether it's RAM replacement, OS repair, or a motherboard fix—without losing your data.",
    pricingText: "$170 Fixed Labor",
    faqs: [
      { question: "Will I lose my files if I have a blue screen?", answer: "Not necessarily. We prioritize data preservation before attempting any operating system repairs." },
      { question: "Can a bad update cause this?", answer: "Yes, failed Windows updates are a common cause of boot loops. We can roll them back safely." }
    ]
  },
  {
    slugSuffix: 'hard-drive-failure',
    titleTemplate: 'Hard Drive Failure Repair {suburb}',
    navLabel: 'Hard Drive Repair',
    icon: Database,
    heroText: "Clicking drive or 'No Boot Device' error? Expert hard drive diagnostics in {suburb}.",
    description: "If your computer says 'No Boot Device Found' or makes clicking noises, your hard drive may be failing. We service {suburb} with emergency drive replacement and data migration services. We can clone your failing drive to a new, fast SSD before it dies completely.",
    pricingText: "$170 Labor + Parts",
    faqs: [
      { question: "My laptop says 'SMART Failure', what does that mean?", answer: "It means the drive is predicting its own death. Back up data immediately and bring it in for replacement." },
      { question: "Do you install SSDs?", answer: "Yes, we replace old mechanical drives with 10x faster SSDs." }
    ]
  },
  {
    slugSuffix: 'power-supply-replacement',
    titleTemplate: 'Power Supply Replacement {suburb}',
    navLabel: 'Power Supply Fix',
    icon: Zap,
    heroText: "PC won't turn on? Expert Power Supply Unit (PSU) replacement in {suburb}.",
    description: "A dead power supply can happen after a storm or power surge. If your desktop PC in {suburb} is completely dead (no lights, no fans), we can test and replace your PSU with a high-quality Gold-rated unit that protects your components.",
    pricingText: "$170 Labor + PSU Cost",
    faqs: [
      { question: "How do I know if it's the power supply?", answer: "If there are absolutely no lights on the motherboard when plugged in, the PSU is the prime suspect." },
      { question: "Do you stock gaming PSUs?", answer: "Yes, we supply modular units suitable for RTX graphics cards." }
    ]
  },
  {
    slugSuffix: 'slow-computer-fix',
    titleTemplate: 'Slow Computer Speed Up {suburb}',
    navLabel: 'Slow PC Fix',
    icon: Activity,
    heroText: "Frustrated by a slow computer in {suburb}? We make old PCs run like new.",
    description: "Don't buy a new computer just yet. Most slow PCs in {suburb} are suffering from bloatware, aging mechanical drives, or thermal throttling. Our 'Supercharge Service' includes deep cleaning, SSD upgrades, and software optimization to restore snappy performance.",
    pricingText: "$170 Fixed Package",
    faqs: [
      { question: "Why is my computer so slow?", answer: "It's usually a combination of startup apps, dust clogging the fans, and an old hard drive." },
      { question: "Is it worth fixing a 5-year-old laptop?", answer: "Absolutely. An SSD upgrade can make a 5-year-old i5 laptop faster than a budget new one." }
    ]
  },
  {
    slugSuffix: 'noisy-fan-repair',
    titleTemplate: 'Noisy Fan & Noise Repair {suburb}',
    navLabel: 'Loud Fan Repair',
    icon: Speaker,
    heroText: "Loud grinding or rattling noises? We fix noisy fans for {suburb} locals.",
    description: "If your computer sounds like a jet engine or a coffee grinder, your cooling fan bearings have failed. Ignoring this can lead to CPU overheating and permanent damage. We replace fans with genuine parts for quiet, efficient cooling in {suburb}.",
    pricingText: "$170 Labor + Fan Cost",
    faqs: [
      { question: "Can I just clean the fan?", answer: "If it's grinding, the bearing is physically worn. Cleaning won't fix the noise; replacement is needed." },
      { question: "Is it dangerous to use with a broken fan?", answer: "Yes, your processor will overheat and throttle performance, or eventually shut down to protect itself." }
    ]
  },
  {
    slugSuffix: 'virus-removal-service',
    titleTemplate: 'Virus & Spyware Removal {suburb}',
    navLabel: 'Virus Removal',
    icon: Bug,
    heroText: "Hacked or scammed in {suburb}? Deep forensic virus removal and security audits.",
    description: "Pop-ups, redirected searches, or 'Microsoft' scam warnings? We provide professional malware removal for {suburb} residents. We don't just scan; we manually verify your registry and startup items to ensure no spyware remains to steal your passwords.",
    pricingText: "$170 Fixed Fee",
    faqs: [
      { question: "Can you remove remote access software?", answer: "Yes, we remove AnyDesk, TeamViewer, and other tools scammers use to access your PC." },
      { question: "Will I lose my files?", answer: "No. We remove the infection while keeping your personal data intact." }
    ]
  },
  {
    slugSuffix: 'graphics-card-repair',
    titleTemplate: 'Visual Glitch & GPU Repair {suburb}',
    navLabel: 'Graphics Repair',
    icon: Monitor,
    heroText: "Visual artifacts, lines, or black screens? GPU diagnostics in {suburb}.",
    description: "Visual glitches (artifacts), pink lines, or crashing during games often point to a Graphics Card (GPU) or display cable issue. We diagnose these complex faults for gamers and designers in {suburb}, offering repairs or upgrades where possible.",
    pricingText: "$170 Diagnostic/Labor",
    faqs: [
      { question: "My screen has colored lines, is it the screen or GPU?", answer: "We connect an external monitor to test. If the lines persist externally, it's the GPU. If not, it's likely your screen." },
      { question: "Do you repair laptop GPUs?", answer: "Laptop GPUs are soldered. We can often reflow them or fix the cooling, but sometimes motherboard repair is needed." }
    ]
  }
];

export const getServiceBySlug = (path: string): { service: ServiceTypeConfig, suburbName: string, formattedSuburb: string } | null => {
  // path example: "newstead-laptop-repairs"
  
  for (const service of SERVICE_TYPES) {
    if (path.endsWith(service.slugSuffix)) {
      // Extract suburb: remove suffix and the hyphen before it
      const rawSuburb = path.substring(0, path.length - service.slugSuffix.length - 1);
      
      // Validation: Ensure rawSuburb isn't empty and looks valid
      if (rawSuburb.length < 3) continue;

      // Format suburb: "new-farm" -> "New Farm"
      const formattedSuburb = rawSuburb
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      return {
        service,
        suburbName: rawSuburb,
        formattedSuburb
      };
    }
  }
  return null;
};